import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const S = "C:\\BotGenomix_MT5\\Scripts";
const GROUPS = [
  { title: "Daily use", items: [
    { id: "start", label: "Start / resume backtest cycles", note: "Clears pause + stop flags and starts the loop (skips if already running).",
      cmd: `powershell -ExecutionPolicy Bypass -File ${S}\\Resume_Brain.ps1` },
    { id: "pause", label: "Pause / stop for the night", note: "Loop exits after the current backtest; watchdog won't restart it until you resume.",
      cmd: `powershell -ExecutionPolicy Bypass -File ${S}\\Pause_Brain.ps1` },
    { id: "status", label: "Is the brain running?", note: "Lists the running brain process (empty = not running).",
      cmd: `Get-CimInstance Win32_Process -Filter "Name='powershell.exe'" | Where-Object { $_.CommandLine -match 'Run_MT5_Brain|Run_Brain_Portable' } | Select-Object ProcessId, CreationDate` },
  ] },
  { title: "Emergency", items: [
    { id: "safe-stop", label: "Hard stop (kill backtest MT5 only)", note: "Kills the portable backtest terminals; your live EA terminal is never touched. Run Pause first so the watchdog doesn't restart it.",
      cmd: `powershell -ExecutionPolicy Bypass -File ${S}\\Pause_Brain.ps1; powershell -ExecutionPolicy Bypass -File ${S}\\Safe_Stop_Brain.ps1` },
  ] },
  { title: "Watchdog (auto-restart every 10 min)", items: [
    { id: "wd-install", label: "Install watchdog (once)", note: "Run in an Administrator PowerShell.",
      cmd: `powershell -ExecutionPolicy Bypass -File ${S}\\Install_Watchdog.ps1` },
    { id: "wd-status", label: "Watchdog status", note: "Last run time and result of the scheduled task.",
      cmd: `Get-ScheduledTask -TaskName BotGenomix_Brain_Watchdog | Get-ScheduledTaskInfo | Select-Object LastRunTime, LastTaskResult, NextRunTime` },
    { id: "wd-log", label: "Watchdog restart log", note: "Every time it had to restart the brain.",
      cmd: `Get-Content C:\\BotGenomix_MT5\\watchdog.log -Tail 20` },
    { id: "wd-remove", label: "Remove watchdog", note: "Turns off auto-restart completely.",
      cmd: `Unregister-ScheduledTask -TaskName BotGenomix_Brain_Watchdog -Confirm:$false` },
  ] },
  { title: "Update scripts (after a new version)", items: [
    { id: "update", label: "Download + install latest scripts (v64)", note: "Pause first, run 'Set bridge token' once, then this, then Start / resume.",
      cmd: `Invoke-WebRequest "https://botgenomix.com/api/downloads/mt5_starter_v64.zip" -Headers @{ Authorization = "Bearer $env:BRIDGE_TOKEN" } -OutFile "$env:TEMP\\v64.zip"; Expand-Archive "$env:TEMP\\v64.zip" ${S} -Force` },
    { id: "store-build", label: "Build store bot files (no brain needed)", note: "Compiles + uploads the protected .ex5 for every listed bot. Safe while a demo terminal is running.",
      cmd: `powershell -ExecutionPolicy Bypass -File "C:\\BotGenomix_MT5\\Scripts\\Build_Store_Files.ps1"` },
    { id: "reports-dryrun", label: "Report cleanup - DRY RUN (deletes nothing)", note: "Checks every old MT5 report against the database and prints what is safe to delete.",
      cmd: `powershell -ExecutionPolicy Bypass -File "C:\\BotGenomix_MT5\\Scripts\\Cleanup_MT5_Reports.ps1"` },
    { id: "disk", label: "Free disk space on C:", note: "Backtest reports are cleaned automatically; check if below ~10 GB.",
      cmd: `Get-PSDrive C | Select-Object @{n='FreeGB';e={[math]::Round($_.Free/1GB,1)}}` },
  ] },
];

const tokenGroup = (t) => ({ title: "Security (run once on the VPS)", items: [
  { id: "set-token", label: "Set bridge token", note: "Lets your VPS scripts talk to botgenomix.com. Keep it private. Open a new PowerShell window afterwards.",
    cmd: `[Environment]::SetEnvironmentVariable("BRIDGE_TOKEN", "${t}", "User"); $env:BRIDGE_TOKEN = "${t}"`,
    shown: `[Environment]::SetEnvironmentVariable("BRIDGE_TOKEN", "${t.slice(0, 4)}••••••••", "User"); ...` },
] });

const CommandRow = ({ item }) => {
  const [copied, setCopied] = useState("");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.cmd);
      setCopied("Copied");
    } catch {
      setCopied("Select text");
    }
    setTimeout(() => setCopied(""), 1500);
  };
  return (
    <div data-testid={`cmd-${item.id}`} className="border border-[#1F2937] rounded-md p-3 bg-[#050914]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm text-[#F1F5F9]">{item.label}</div>
          <div className="mono text-[10px] text-[#64748B] mt-0.5">{item.note}</div>
        </div>
        <button data-testid={`cmd-${item.id}-copy`} onClick={copy}
          className="shrink-0 mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded border border-[#2ECA7F]/40 text-[#2ECA7F] hover:bg-[#2ECA7F] hover:text-[#050914] transition-colors">
          {copied || "Copy"}
        </button>
      </div>
      <pre className="mt-2 mono text-[10px] text-[#22D3EE] whitespace-pre-wrap break-all">{item.shown || item.cmd}</pre>
    </div>
  );
};

export const CommandsCard = () => {
  const [token, setToken] = useState(null);
  useEffect(() => { api.get("/admin/bridge-token").then((r) => setToken(r.data.token)).catch(() => {}); }, []);
  const groups = token ? [tokenGroup(token), ...GROUPS] : GROUPS;
  return (
  <div data-testid="commands-card" className="mt-8 border border-[#2ECA7F]/30 rounded-lg bg-[#0B0D14]">
    <div className="px-5 py-3 border-b border-[#2ECA7F]/30 flex items-center justify-between">
      <div className="text-lg font-medium">VPS commands</div>
      <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Paste into PowerShell on the VPS</div>
    </div>
    <div className="p-5 space-y-5">
      {groups.map((g) => (
        <div key={g.title}>
          <div className="mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">{g.title}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {g.items.map((it) => <CommandRow key={it.id} item={it} />)}
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};
